import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { getCounter } from "@/lib/warRoom/eventCounters";
import { trackEvent } from "@/lib/audit";
import {
  COUNTRY_EXPERIENCE_REGISTRY,
  SUPPORTED_COUNTRIES,
  COUNTRY_HOME_PATH,
  type CountryCode,
} from "@/lib/country/countryExperienceEngine";
import { listInfluencersByCountry } from "@/lib/growth/influencerRegistry";

export const Route = createFileRoute("/admin/country-growth")({
  head: () => ({
    meta: [
      { title: "Country Growth — VerseSport" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CountryGrowthDashboard,
});

function CountryGrowthDashboard() {
  useEffect(() => { void trackEvent("country_growth_dashboard_opened", {}); }, []);

  return (
    <main className="mx-auto max-w-5xl space-y-4 px-3 py-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-black tracking-tight">Country Growth Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Per-device telemetry from the existing War Room counters. No new tables, no fake data.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {SUPPORTED_COUNTRIES.map((code) => (
          <CountryCard key={code} code={code} />
        ))}
      </section>
    </main>
  );
}

function CountryCard({ code }: { code: CountryCode }) {
  const exp = COUNTRY_EXPERIENCE_REGISTRY[code];
  const influencers = listInfluencersByCountry(code);
  const kpis = [
    { label: "Hub views", value: getCounter("country_hub_view") },
    { label: "Experience views", value: getCounter("country_experience_view") },
    { label: "CTA clicks", value: getCounter("country_cta_clicked") },
    { label: "Broadcast clicks", value: getCounter("country_broadcast_clicked") },
    { label: "Shares (WA)", value: getCounter("whatsapp_growth_cta_clicked") },
    { label: "Telegram", value: getCounter("telegram_channel_cta_clicked") },
    { label: "Referrals", value: getCounter("first_referral_sent") },
    { label: "Challenge friend", value: getCounter("challenge_friend_sent") },
    { label: "Daily WOW shares", value: getCounter("daily_wow_shared") },
    { label: "Return same-day", value: getCounter("returned_same_day") },
  ];

  return (
    <article
      className="rounded-2xl border p-3"
      style={{
        borderColor: `${exp.accent_colors.primary}55`,
        background: `linear-gradient(135deg, ${exp.accent_colors.primary}0F, transparent)`,
      }}
    >
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-black">
          {exp.flag} {exp.country_name}
        </h2>
        <Link
          to={COUNTRY_HOME_PATH[code] as never}
          className="text-[10px] font-bold uppercase tracking-widest underline"
        >
          Ver hub
        </Link>
      </header>
      <ul className="grid grid-cols-2 gap-1.5">
        {kpis.map((k) => (
          <li key={k.label} className="rounded-lg border border-border bg-background/60 p-2">
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
              {k.label}
            </div>
            <div className="text-lg font-black leading-tight">{k.value}</div>
          </li>
        ))}
      </ul>
      <footer className="mt-2 text-[10px] text-muted-foreground">
        {influencers.length} influencer{influencers.length === 1 ? "" : "s"} ·{" "}
        {exp.broadcast_partners.length} broadcaster
        {exp.broadcast_partners.length === 1 ? "" : "s"}
      </footer>
    </article>
  );
}